/**
 * Keyboard Navigation Hook
 *
 * Provides keyboard navigation for workspace items in grid and list views.
 * Integrates with useSelectionStore for unified selection state.
 *
 * Features:
 * - Arrow key navigation (up/down for list, up/down/left/right for grid)
 * - Enter to open item
 * - Space to toggle preview (open if closed, close if open)
 * - Delete/Backspace to move to trash
 * - Cmd/Ctrl+A to select all
 * - Escape to clear selection
 * - Shift+Arrow for range selection
 * - Auto-preview on arrow key navigation (non-folders only)
 *
 * @module features/workspace/hooks/useKeyboardNavigation
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { WorkspaceItemType } from '@loader/shared-types';

import type { WorkspaceItem, ViewMode } from '../components/FolderView/types';
import { useSelectionStore } from '../stores/useSelectionStore';
import { useSidePanelStore } from '../stores/useSidePanelStore';

// ============================================================================
// TYPES
// ============================================================================

export interface UseKeyboardNavigationOptions {
  /** Items to navigate through */
  items: WorkspaceItem[];
  /** Current view mode (affects arrow key behavior) */
  viewMode: ViewMode;
  /** Number of columns in grid view (for left/right navigation) */
  gridColumns?: number;
  /** Whether keyboard navigation is enabled */
  enabled?: boolean;
  /** Preview handler */
  onPreview?: (id: string, type: WorkspaceItemType) => void;
  /** Delete handler */
  onDelete?: (id: string, type: WorkspaceItemType, name?: string) => void;
}

export interface UseKeyboardNavigationResult {
  /** Currently focused item index */
  focusedIndex: number | null;
  /** Set focused index */
  setFocusedIndex: (index: number | null) => void;
  /** Whether an item is focused */
  isFocused: (index: number) => boolean;
  /** Key down handler to attach to container */
  handleKeyDown: (e: React.KeyboardEvent) => void;
  /** Focus an item programmatically */
  focusItem: (index: number) => void;
  /** Clear focus */
  clearFocus: () => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if the user is currently typing in an input field
 */
function isTypingInInput(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable
  );
}

/**
 * Calculate the next index based on direction and view mode
 */
function calculateNextIndex(
  currentIndex: number,
  direction: 'up' | 'down' | 'left' | 'right',
  viewMode: ViewMode,
  gridColumns: number,
  itemCount: number
): number {
  // Handle initial selection case (no current focus)
  if (currentIndex === -1) {
    return 0;
  }

  let newIndex: number;

  switch (direction) {
    case 'up':
      if (viewMode === 'list') {
        // List view: move one item up
        newIndex = Math.max(0, currentIndex - 1);
      } else {
        // Grid view: move one row up
        newIndex = currentIndex - gridColumns;
        if (newIndex < 0) newIndex = currentIndex; // Stay at current if at top
      }
      break;

    case 'down':
      if (viewMode === 'list') {
        // List view: move one item down
        newIndex = Math.min(itemCount - 1, currentIndex + 1);
      } else {
        // Grid view: move one row down
        newIndex = currentIndex + gridColumns;
        if (newIndex >= itemCount) newIndex = currentIndex; // Stay at current if at bottom
      }
      break;

    case 'left':
      // Grid view only: move one item left
      newIndex = Math.max(0, currentIndex - 1);
      break;

    case 'right':
      // Grid view only: move one item right
      newIndex = Math.min(itemCount - 1, currentIndex + 1);
      break;

    default:
      newIndex = currentIndex;
  }

  return newIndex;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for keyboard navigation in workspace items
 * Uses useSelectionStore for unified selection state with mouse clicks
 */
export function useKeyboardNavigation({
  items,
  viewMode,
  gridColumns = 8,
  enabled = true,
  onPreview,
  onDelete,
}: UseKeyboardNavigationOptions): UseKeyboardNavigationResult {
  const navigate = useNavigate();
  const previewTimeoutRef = useRef<number | null>(null);

  // Selection store for unified selection state
  const {
    selectedIds,
    lastSelectedId,
    selectItem,
    selectRange,
    selectAll,
    clearSelection,
  } = useSelectionStore();

  // Side panel store to check if preview is open and to expand overlay
  const { isOpen: sidePanelIsOpen, mode: sidePanelMode, close: closeSidePanel, openPreview, expand } = useSidePanelStore();

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current !== null) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, []);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Get all item IDs for select-all operations
  const allItemIds = useMemo(() => items.map((item) => item.id), [items]);

  // Get the current focused index based on selection
  const focusedIndex = useMemo(() => {
    if (selectedIds.size === 0) return null;
    // Use lastSelectedId to determine focus, or first selected item
    const focusId = lastSelectedId || Array.from(selectedIds)[0];
    const index = items.findIndex((item) => item.id === focusId);
    return index >= 0 ? index : null;
  }, [selectedIds, lastSelectedId, items]);

  // Check if preview is currently open
  const isPreviewOpen = sidePanelIsOpen && (sidePanelMode === 'previewTable' || sidePanelMode === 'previewLinkedTable');

  // ============================================================================
  // NAVIGATION HELPERS
  // ============================================================================

  /**
   * Open the focused item
   * - Folders: navigate to folder view
   * - Tables/Linked Tables: open expanded overlay
   */
  const openFocusedItem = useCallback(() => {
    if (focusedIndex === null || focusedIndex >= items.length) return;
    const item = items[focusedIndex];

    if (item.type === 'folder') {
      // Navigate to folder view
      void navigate({ to: '/workspace/folder/$id', params: { id: item.id } });
    } else {
      // Open expanded overlay for tables/linked_tables
      openPreview({
        id: item.id,
        type: item.type,
        name: item.name,
        description: item.description,
        isFavorite: item.isFavorite,
        updatedAt: item.updatedAt,
        createdAt: item.createdAt,
        connectorType: item.connectorType,
      });
      expand();
    }
  }, [focusedIndex, items, navigate, openPreview, expand]);

  /**
   * Trigger preview for a specific item (if not a folder)
   */
  const triggerPreview = useCallback(
    (item: WorkspaceItem) => {
      // Don't preview folders
      if (item.type === 'folder') return;

      // Clear any pending preview timeout
      if (previewTimeoutRef.current !== null) {
        clearTimeout(previewTimeoutRef.current);
      }

      // Trigger preview with a small delay to avoid rapid updates during arrow key navigation
      previewTimeoutRef.current = setTimeout(() => {
        onPreview?.(item.id, item.type);
      }, 50) as unknown as number;
    },
    [onPreview]
  );

  /**
   * Toggle preview for the focused item
   * If preview is open, close it. If closed, open it.
   */
  const togglePreview = useCallback(() => {
    if (focusedIndex === null || focusedIndex >= items.length) return;
    const item = items[focusedIndex];

    // Don't preview folders
    if (item.type === 'folder') return;

    if (isPreviewOpen) {
      // Close preview if it's open
      closeSidePanel();
    } else {
      // Open preview if it's closed
      onPreview?.(item.id, item.type);
    }
  }, [focusedIndex, items, onPreview, isPreviewOpen, closeSidePanel]);

  /**
   * Delete the focused item
   */
  const deleteFocusedItem = useCallback(() => {
    if (focusedIndex === null || focusedIndex >= items.length) return;
    const item = items[focusedIndex];
    onDelete?.(item.id, item.type, item.name);
  }, [focusedIndex, items, onDelete]);

  // ============================================================================
  // SELECTION MOVEMENT
  // ============================================================================

  /**
   * Move selection in a direction
   * Also triggers preview for the newly selected item (unless it's a folder)
   */
  const moveSelection = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right', withShift: boolean) => {
      if (items.length === 0) return;

      const currentIndex = focusedIndex ?? -1;
      const newIndex = calculateNextIndex(currentIndex, direction, viewMode, gridColumns, items.length);

      // No change in index
      if (newIndex === currentIndex && currentIndex !== -1) return;

      // Select the new item
      if (newIndex >= 0 && newIndex < items.length) {
        const newItem = items[newIndex];

        if (withShift) {
          // Shift+Arrow: extend selection range
          selectRange(newItem.id, allItemIds);
        } else {
          // Regular arrow: single selection
          selectItem(newItem.id);
        }

        // Trigger preview for the newly selected item (if not a folder and not in range select mode)
        if (!withShift) {
          triggerPreview(newItem);
        }
      }
    },
    [items, focusedIndex, viewMode, gridColumns, selectItem, selectRange, allItemIds, triggerPreview]
  );

  // ============================================================================
  // KEYBOARD EVENT HANDLER
  // ============================================================================

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!enabled || items.length === 0) return;

      // Don't handle keyboard shortcuts if user is typing in an input field
      if (isTypingInInput(e.target)) {
        return;
      }

      switch (e.key) {
        // ===== ARROW KEY NAVIGATION =====
        case 'ArrowUp':
          e.preventDefault();
          moveSelection('up', e.shiftKey);
          break;

        case 'ArrowDown':
          e.preventDefault();
          moveSelection('down', e.shiftKey);
          break;

        case 'ArrowLeft':
          // Only handle left arrow in grid view
          if (viewMode === 'grid') {
            e.preventDefault();
            moveSelection('left', e.shiftKey);
          }
          break;

        case 'ArrowRight':
          // Only handle right arrow in grid view
          if (viewMode === 'grid') {
            e.preventDefault();
            moveSelection('right', e.shiftKey);
          }
          break;

        // ===== ACTION KEYS =====
        case 'Enter':
          // Open the focused item (folder: navigate, table/linked_table: expand overlay)
          e.preventDefault();
          openFocusedItem();
          break;

        case ' ':
          // Toggle preview (open if closed, close if open)
          e.preventDefault();
          togglePreview();
          break;

        case 'Delete':
        case 'Backspace':
          // Delete focused item
          // Only delete on Delete key, or Backspace when not in an input
          if (e.key === 'Delete' || !isTypingInInput(e.target)) {
            e.preventDefault();
            deleteFocusedItem();
          }
          break;

        case 'Escape':
          // Clear selection
          e.preventDefault();
          clearSelection();
          break;

        // ===== MULTI-SELECT KEYS =====
        case 'a':
          // Cmd/Ctrl+A to select all
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            selectAll(allItemIds);
          }
          break;
      }
    },
    [
      enabled,
      items.length,
      viewMode,
      moveSelection,
      openFocusedItem,
      togglePreview,
      deleteFocusedItem,
      clearSelection,
      selectAll,
      allItemIds,
    ]
  );

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Check if an item is focused
   */
  const isFocused = useCallback(
    (index: number) => {
      return focusedIndex === index;
    },
    [focusedIndex]
  );

  /**
   * Focus an item programmatically (selects it)
   */
  const focusItem = useCallback(
    (index: number) => {
      if (index >= 0 && index < items.length) {
        const item = items[index];
        selectItem(item.id);
        // Also trigger preview for the newly focused item
        triggerPreview(item);
      }
    },
    [items, selectItem, triggerPreview]
  );

  /**
   * Clear focus (clears selection)
   */
  const clearFocus = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  /**
   * Set focused index (selects item at index)
   */
  const setFocusedIndex = useCallback(
    (index: number | null) => {
      if (index === null) {
        clearSelection();
      } else if (index >= 0 && index < items.length) {
        const item = items[index];
        selectItem(item.id);
        // Also trigger preview for the newly focused item
        triggerPreview(item);
      }
    },
    [items, selectItem, clearSelection, triggerPreview]
  );

  return {
    focusedIndex,
    setFocusedIndex,
    isFocused,
    handleKeyDown,
    focusItem,
    clearFocus,
  };
}
